import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Indicator,
  Kbd,
  Loader,
  Menu,
  Modal,
  Paper,
  Popover,
  RingProgress,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconCopy,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandWhatsapp,
  IconCheck,
  IconChecks,
  IconChevronRight,
  IconCirclePlus,
  IconCircleDot,
  IconClipboardText,
  IconClock,
  IconDotsVertical,
  IconFilter,
  IconMoodHappy,
  IconMoodSmile,
  IconPaperclip,
  IconPhoto,
  IconUpload,
  IconPin,
  IconPinFilled,
  IconSearch,
  IconSend,
  IconSparkles,
  IconTemplate,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useDebouncedValue, useDisclosure, useLocalStorage } from '@mantine/hooks';
import { SendTemplateModal } from '../../components/chat/SendTemplateModal';
import { motion, AnimatePresence } from 'framer-motion';
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { trpc, type ThreadMessage } from '../../lib/trpc';
import { useChatRealtime, useChatThreadRealtime, useTypingIndicator } from '../../lib/realtime';
import {
  THREAD_PAGE_LIMIT,
  TIP_FETCH_OPTS,
  flattenThread,
  mergeNewestPage,
  removeMessageFromThread,
  upsertMessage,
} from '../../lib/thread-cache';
import { useThreadScroll } from '../../components/inbox/useThreadScroll';
import { useSocketStatus } from '../../lib/socket';
import { PermissionGate } from '../../components/PermissionGate';
import { QuickRepliesPopover } from '../../components/QuickRepliesPopover';
import { CrmPanel } from '../../components/inbox/CrmPanel';
import { MediaLibraryPicker } from '../../components/inbox/MediaLibraryPicker';
import { CollapseRail } from '../../components/CollapseRail';
import { WaMessageBody, WaMessageCard } from '../../components/inbox/registries/whatsapp-content';
import { normalizeWaMessage, waPreviewText } from '../../lib/whatsapp-message';
// `formatTime`/`timeAgo` used to be re-declared here verbatim. `dayLabel`,
// `groupMessages`, `DateDivider` and `ReadReceipt` stay local on purpose: this
// thread groups by burst-with-label (a different shape) and its ticks are
// tuned for the blue outbound bubble, where the shared light-bubble palette
// would be invisible.
import { formatTime, timeAgo } from '../../lib/chat-format';

// emoji-picker-react ships ~200KB gzipped of unicode data — keep it out of
// the initial chat-route bundle.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

type ChannelKind = 'whatsapp' | 'messenger' | 'instagram';
type FilterKey = 'all' | 'unread' | 'mine' | 'unassigned' | 'closing';

const CHANNEL_META: Record<
  ChannelKind,
  { label: string; Icon: typeof IconBrandWhatsapp; color: string }
> = {
  whatsapp: { label: 'WhatsApp', Icon: IconBrandWhatsapp, color: 'teal' },
  messenger: { label: 'Messenger', Icon: IconBrandFacebook, color: 'blue' },
  instagram: { label: 'Instagram', Icon: IconBrandInstagram, color: 'pink' },
};

function ChannelChip({ channel, size = 12 }: { channel: ChannelKind; size?: number }) {
  const { Icon, color, label } = CHANNEL_META[channel];
  return (
    <Tooltip label={label}>
      <Icon size={size} color={`var(--mantine-color-${color}-6)`} />
    </Tooltip>
  );
}

/** Cross-channel identity fields a contact may carry (WhatsApp contacts have a
 *  waId; FB/IG contacts have an IG username / scoped id / FB page-scoped id). */
type ContactIdentity = {
  fullName?: string | null;
  waId?: string | null;
  instagramUsername?: string | null;
  instagramScopedId?: string | null;
  facebookPageScopedId?: string | null;
};

/** Best display name across channels: profile name → WhatsApp id → IG handle →
 *  IG/FB scoped id. Mirrors the sidebar row fallback so the header, avatar, and
 *  CRM panel never collapse to "—" for an FB/IG contact whose waId is null. */
function contactDisplayName(c: ContactIdentity): string {
  return (
    c.fullName ||
    c.waId ||
    c.instagramUsername ||
    c.instagramScopedId ||
    c.facebookPageScopedId ||
    ''
  );
}

/** The monospace identifier line under the name — WhatsApp number, else the
 *  IG handle / FB page-scoped id. */
function contactIdentifier(c: ContactIdentity): string | null {
  return c.waId ?? c.instagramUsername ?? c.facebookPageScopedId ?? null;
}

/* ───────────────────────── ChatPage ───────────────────────── */

function ChatPage() {
  useChatRealtime();
  // Socket status drives the "Reconnecting…" pill in the sidebar header so
  // the user knows real-time delivery is currently down (and the 30s
  // polling backstop on `chat.contacts` is what's keeping them current).
  const socketStatus = useSocketStatus();
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 250);
  const [activeContactId, setActiveContactId] = useState<number | null>(null);
  // Deep-link: open a specific contact's thread when navigated here with
  // ?contactId (e.g. the "Open WhatsApp" button on a contact detail). The WA
  // inbox is keyed by Contact.id, so the param is the selection id directly.
  const deepLink = Route.useSearch();
  useEffect(() => {
    if (deepLink.contactId) setActiveContactId(deepLink.contactId);
  }, [deepLink.contactId]);
  const [crmOpened, { toggle: toggleCrm }] = useDisclosure(true);
  // Left conversation-list collapse — persisted and shared with the FB/IG
  // inboxes so the layout preference sticks across reloads + channels.
  const [leftCollapsed, setLeftCollapsed] = useLocalStorage<boolean>({
    key: 'leadsync.inbox.leftCollapsed',
    defaultValue: false,
  });
  // Sidebar label/group filter — opened via the filter ActionIcon. Backend
  // accepts labelId + groupId on `chat.contacts`; the popover wires them up.
  const [filterLabelId, setFilterLabelId] = useState<number | null>(null);
  const [filterGroupId, setFilterGroupId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'unread' | 'alpha'>('recent');
  const [filterPopoverOpen, { toggle: toggleFilterPopover, close: closeFilterPopover }] =
    useDisclosure(false);
  // Mobile sidebar collapse — auto-show on mount when no contact is open,
  // collapse on contact pick so the conversation pane gets full width.
  const [mobileNavOpen, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const labelsList = trpc.contacts.labels.list.useQuery(undefined, { staleTime: 5 * 60_000 });
  const groupsList = trpc.contacts.groups.list.useQuery(undefined, { staleTime: 5 * 60_000 });

  // The server tab arg accepts only 'all' / 'mine' / 'unassigned'; the UI
  // chips translate to those plus a client-side post-filter for unread /
  // closing. Keeps the API stable.
  const tabArg: 'all' | 'mine' | 'unassigned' =
    filter === 'mine' ? 'mine' : filter === 'unassigned' ? 'unassigned' : 'all';

  const contacts = trpc.chat.contacts.useQuery(
    {
      tab: tabArg,
      search: debounced || undefined,
      labelId: filterLabelId ?? undefined,
      groupId: filterGroupId ?? undefined,
      sortBy,
      page: 1,
      pageSize: 100,
      unreadOnly: filter === 'unread',
    },
    {
      // 30s polling backstop — if the Socket.IO real-time path is down
      // (auth issue, network, reverse-proxy mis-config), the sidebar
      // still freshens within 30s instead of being stuck stale until
      // the user hits refresh.
      refetchInterval: 30_000,
      refetchIntervalInBackground: true,
    },
  );

  // Sidebar drafts — read at this level so each row can show a "Draft:"
  // indicator without each row re-reading localStorage.
  const [drafts] = useLocalStorage<Record<string, string>>({
    key: 'leadsync.chat.drafts',
    defaultValue: {},
  });

  const items = contacts.data?.items ?? [];

  // closing-soon = WhatsApp contacts whose last inbound is between 22h and
  // 24h ago (window open but ≤2h left). The list query doesn't surface
  // windowClosesInMinutes today, so we approximate from lastIncomingAt.
  const filtered = useMemo(() => {
    if (filter !== 'closing') return items;
    const now = Date.now();
    return items.filter((c) => {
      if (c.channel !== 'whatsapp' || !c.lastIncomingAt) return false;
      const t = new Date(c.lastIncomingAt).getTime();
      return t < now - 22 * 60 * 60 * 1000 && t > now - 24 * 60 * 60 * 1000;
    });
  }, [items, filter]);

  const counts = useMemo(() => {
    const unread = items.reduce((n, c) => n + (c.unreadCount > 0 ? 1 : 0), 0);
    const mine = items.filter((c) => c.assignedToMe).length;
    const unassigned = items.filter((c) => !c.assignedUserId).length;
    return { unread, mine, unassigned };
  }, [items]);

  const pinned = filtered.filter((c) => c.pinnedAt != null);
  const rest = filtered.filter((c) => c.pinnedAt == null);

  // Keyboard nav across the sidebar — ↑/↓ walks the merged [pinned, rest]
  // list, skipping when a text input is focused so the composer / search /
  // notes textarea keep ownership of those keys.
  useEffect(() => {
    const list = [...pinned, ...rest];
    if (list.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t instanceof HTMLInputElement ||
          t instanceof HTMLTextAreaElement ||
          t.isContentEditable ||
          t.getAttribute('role') === 'textbox')
      ) {
        return;
      }
      e.preventDefault();
      const idx = list.findIndex((c) => c.id === activeContactId);
      const next =
        e.key === 'ArrowDown'
          ? list[idx < 0 ? 0 : Math.min(list.length - 1, idx + 1)]
          : list[idx <= 0 ? 0 : idx - 1];
      if (next) setActiveContactId(next.id);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pinned, rest, activeContactId]);

  const showCrmCol = !!activeContactId && crmOpened;

  // Mobile responsive: hide the 340px sidebar at <768px unless the user
  // toggles it open via the hamburger; auto-close after picking a contact.
  const handleContactSelect = useCallback(
    (id: number) => {
      setActiveContactId(id);
      closeMobileNav();
    },
    [closeMobileNav],
  );

  return (
    <AppShell.Section>
      <Box
        className={mobileNavOpen ? 'chat-nav-mobile-open' : 'chat-nav-mobile-closed'}
        style={{
          display: 'grid',
          gridTemplateColumns: `${activeContactId && leftCollapsed ? '0px' : '340px'} 1fr ${showCrmCol ? '320px' : '0px'}`,
          gridTemplateRows: 'minmax(0, 1fr)',
          gap: 8,
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          minHeight: 0,
          overflow: 'hidden',
          transition: 'grid-template-columns 220ms ease',
        }}
      >
        {/* ── Sidebar ─────────────────────────────────────── */}
        <Card p={0} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Stack gap="xs" p="sm" pb="xs">
            <Group justify="space-between" align="center">
              <Group gap={6} align="center">
                <Title order={4}>{t('Chat_Inbox')}</Title>
                {socketStatus !== 'connected' && (
                  <Badge
                    size="xs"
                    variant="light"
                    color={socketStatus === 'forbidden' ? 'red' : 'yellow'}
                    aria-label="Real-time connection status"
                  >
                    {socketStatus === 'forbidden'
                      ? 'Realtime: no access'
                      : socketStatus === 'connecting'
                        ? 'Connecting…'
                        : 'Reconnecting…'}
                  </Badge>
                )}
              </Group>
              <Group gap={4}>
                <Select
                  size="xs"
                  w={120}
                  aria-label="Sort contacts"
                  value={sortBy}
                  onChange={(v) => setSortBy((v as 'recent' | 'unread' | 'alpha') ?? 'recent')}
                  data={[
                    { value: 'recent', label: 'Most recent' },
                    { value: 'unread', label: 'Unread first' },
                    { value: 'alpha', label: 'A–Z' },
                  ]}
                  allowDeselect={false}
                />
                <Popover
                  opened={filterPopoverOpen}
                  onClose={closeFilterPopover}
                  position="bottom-end"
                  width={260}
                  withArrow
                  shadow="md"
                >
                  <Popover.Target>
                    <Tooltip label="Filter contacts by label or group">
                      <ActionIcon
                        variant={filterLabelId || filterGroupId ? 'filled' : 'subtle'}
                        color="blue"
                        size="sm"
                        onClick={toggleFilterPopover}
                        aria-label="Filter contacts"
                      >
                        <IconFilter size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Stack gap="xs">
                      <Select
                        label="Label"
                        placeholder="Any label"
                        clearable
                        searchable
                        data={(labelsList.data ?? []).map((l) => ({
                          value: String(l.id),
                          label: l.title,
                        }))}
                        value={filterLabelId ? String(filterLabelId) : null}
                        onChange={(v) => setFilterLabelId(v ? Number(v) : null)}
                      />
                      <Select
                        label="Group"
                        placeholder="Any group"
                        clearable
                        searchable
                        data={(groupsList.data ?? []).map((g) => ({
                          value: String(g.id),
                          label: g.title,
                        }))}
                        value={filterGroupId ? String(filterGroupId) : null}
                        onChange={(v) => setFilterGroupId(v ? Number(v) : null)}
                      />
                      {(filterLabelId || filterGroupId) && (
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => {
                            setFilterLabelId(null);
                            setFilterGroupId(null);
                          }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
              </Group>
            </Group>
            <TextInput
              placeholder={t('Chat_SearchPlaceholder')}
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              size="sm"
            />
            <FilterChips value={filter} onChange={setFilter} counts={counts} />
          </Stack>
          <Divider />
          <ScrollArea style={{ flex: 1 }} scrollbarSize={6}>
            {contacts.isPending ? (
              <Stack p="sm" gap="xs">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} height={56} radius="sm" />
                ))}
              </Stack>
            ) : contacts.isError ? (
              <Text c="red" p="md" size="sm">
                {contacts.error.message}
              </Text>
            ) : filtered.length === 0 ? (
              <EmptyInbox filter={filter} />
            ) : (
              <Stack gap={2} py={4}>
                {pinned.length > 0 && (
                  <>
                    <SectionLabel>Pinned</SectionLabel>
                    {pinned.map((c) => (
                      <ContactRow
                        key={c.id}
                        contact={c}
                        active={c.id === activeContactId}
                        draftText={drafts[String(c.id)]}
                        onSelect={handleContactSelect}
                      />
                    ))}
                    <SectionLabel>All conversations</SectionLabel>
                  </>
                )}
                {rest.map((c) => (
                  <ContactRow
                    key={c.id}
                    contact={c}
                    active={c.id === activeContactId}
                    draftText={drafts[String(c.id)]}
                    onSelect={handleContactSelect}
                  />
                ))}
              </Stack>
            )}
          </ScrollArea>
          <Divider />
          <Group p="xs" gap={8} c="dimmed">
            <Text size="xs">
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> navigate · <Kbd>/</Kbd> templates
            </Text>
          </Group>
        </Card>

        {/* ── Conversation pane ───────────────────────────── */}
        <Box
          className="collapse-pane"
          style={{
            position: 'relative',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {activeContactId ? (
            <Conversation
              contactId={activeContactId}
              channel={
                (items.find((c) => c.id === activeContactId)?.channel ?? 'whatsapp') as ChannelKind
              }
              onOpenSidebar={toggleMobileNav}
            />
          ) : (
            <EmptyConversation />
          )}
          {activeContactId && (
            <>
              <CollapseRail
                side="left"
                collapsed={leftCollapsed}
                onToggle={() => setLeftCollapsed((v) => !v)}
                label="conversation list"
              />
              <CollapseRail
                side="right"
                collapsed={!crmOpened}
                onToggle={toggleCrm}
                label="contact panel"
              />
            </>
          )}
        </Box>

        {/* ── CRM panel ───────────────────────────────────── */}
        <Box style={{ overflow: 'hidden', minWidth: 0, minHeight: 0 }}>
          {activeContactId && crmOpened && <CrmPanel contactId={activeContactId} />}
        </Box>
      </Box>
    </AppShell.Section>
  );
}

/* ───────────────────────── Sidebar bits ───────────────────────── */

function FilterChips({
  value,
  onChange,
  counts,
}: {
  value: FilterKey;
  onChange: (v: FilterKey) => void;
  counts: { unread: number; mine: number; unassigned: number };
}) {
  const chips: { key: FilterKey; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread', count: counts.unread },
    { key: 'mine', label: 'Mine', count: counts.mine },
    { key: 'unassigned', label: 'Unassigned', count: counts.unassigned },
    { key: 'closing', label: 'Closing soon' },
  ];
  return (
    <Group gap={4} wrap="wrap">
      {chips.map((c) => {
        const active = value === c.key;
        return (
          <UnstyledButton
            key={c.key}
            onClick={() => onChange(c.key)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 500,
              background: active ? 'var(--mantine-color-blue-light)' : 'transparent',
              color: active ? 'var(--mantine-color-blue-7)' : 'var(--mantine-color-dimmed)',
              border: `1px solid ${
                active ? 'var(--mantine-color-blue-3)' : 'var(--mantine-color-gray-3)'
              }`,
              transition: 'all 120ms',
            }}
          >
            {c.label}
            {c.count != null && c.count > 0 && (
              <Box
                component="span"
                style={{
                  marginLeft: 6,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: active
                    ? 'var(--mantine-color-blue-6)'
                    : 'var(--mantine-color-gray-4)',
                  color: 'white',
                  fontSize: 10,
                }}
              >
                {c.count}
              </Box>
            )}
          </UnstyledButton>
        );
      })}
    </Group>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      size="xs"
      fw={600}
      c="dimmed"
      tt="uppercase"
      style={{ letterSpacing: 0.5, padding: '8px 12px 4px' }}
    >
      {children}
    </Text>
  );
}

type ContactItem = {
  id: number;
  fullName: string;
  waId: string | null;
  instagramUsername?: string | null;
  instagramScopedId?: string | null;
  facebookPageScopedId?: string | null;
  channel: ChannelKind;
  lastMessage: string | null;
  lastMessageAt: Date | string | null;
  lastIncomingAt: Date | string | null;
  unreadCount: number;
  pinnedAt: Date | string | null;
  assignedUserId: string | null;
  // The contacts list builds labels with `.filter(Boolean)`, but TS can't
  // narrow that — the inferred element type stays `{...} | null`. Mirror it
  // here and dereference defensively at the call sites.
  labels: ({
    id: number;
    title: string;
    textColor: string | null;
    bgColor: string | null;
  } | null)[];
};

const ContactRow = memo(function ContactRow({
  contact: c,
  active,
  draftText,
  onSelect,
}: {
  contact: ContactItem;
  active: boolean;
  draftText?: string;
  onSelect: (id: number) => void;
}) {
  const channel = c.channel;
  const display =
    c.fullName ||
    c.waId ||
    c.instagramUsername ||
    c.instagramScopedId ||
    c.facebookPageScopedId ||
    '—';
  const initial = display[0]?.toUpperCase() ?? '?';
  const labelBg = c.labels[0]?.bgColor ?? null;
  const hasDraft = !!draftText && draftText.trim().length > 0;
  const isPinned = c.pinnedAt != null;

  return (
    <UnstyledButton
      onClick={() => onSelect(c.id)}
      style={{
        padding: '10px 12px',
        // `light-dark()` so the contact row reads correctly in both modes —
        // mirrors the same pattern the message bubble uses (lines ~1189).
        background: active
          ? 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-5))'
          : 'transparent',
        borderLeft: `3px solid ${active ? 'var(--mantine-color-blue-6)' : 'transparent'}`,
        transition: 'background 120ms',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!active)
          e.currentTarget.style.background =
            'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <Group wrap="nowrap" gap="sm" align="flex-start">
        <Indicator
          inline
          size={10}
          offset={4}
          color={CHANNEL_META[channel].color}
          withBorder
          position="bottom-end"
          label={null}
          disabled={false}
        >
          <Avatar
            size={40}
            radius="xl"
            style={labelBg ? { background: labelBg, color: 'white' } : undefined}
          >
            {initial}
          </Avatar>
        </Indicator>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap" gap={6}>
            <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
              {isPinned && (
                <IconPinFilled
                  size={11}
                  style={{ color: 'var(--mantine-color-blue-6)', flexShrink: 0 }}
                />
              )}
              <Text size="sm" fw={c.unreadCount > 0 ? 700 : 500} truncate>
                {display}
              </Text>
            </Group>
            <Text
              size="xs"
              c={c.unreadCount > 0 ? 'blue.6' : 'dimmed'}
              fw={c.unreadCount > 0 ? 600 : 400}
              style={{ flexShrink: 0 }}
            >
              {c.lastMessageAt ? timeAgo(new Date(c.lastMessageAt)) : ''}
            </Text>
          </Group>
          <Group justify="space-between" wrap="nowrap" gap={6} mt={2}>
            <Text
              size="xs"
              c={c.unreadCount > 0 ? 'gray.8' : 'dimmed'}
              fw={c.unreadCount > 0 ? 500 : 400}
              truncate
              style={{ flex: 1 }}
            >
              {hasDraft ? (
                <>
                  <Text component="span" c="red.6" fw={600} inherit>
                    Draft:
                  </Text>{' '}
                  {draftText}
                </>
              ) : (
                (c.lastMessage ?? '—')
              )}
            </Text>
            {c.unreadCount > 0 && (
              <Badge size="sm" circle variant="filled" color="blue">
                {c.unreadCount}
              </Badge>
            )}
          </Group>
        </Box>
      </Group>
    </UnstyledButton>
  );
});

function EmptyInbox({ filter }: { filter: FilterKey }) {
  const copy: Record<FilterKey, string> = {
    all: 'No conversations yet.',
    unread: "You're all caught up.",
    mine: 'Nothing assigned to you.',
    unassigned: 'No unassigned conversations.',
    closing: 'No conversations approaching their 24h window close.',
  };
  return (
    <Stack align="center" py="xl" px="md" gap={4}>
      <ThemeIcon variant="light" color="gray" size={42} radius="xl">
        <IconCircleDot size={20} />
      </ThemeIcon>
      <Text size="sm" c="dimmed" ta="center">
        {copy[filter]}
      </Text>
    </Stack>
  );
}

function EmptyConversation() {
  const { t } = useTranslation();
  return (
    <Card style={{ flex: 1, display: 'flex' }}>
      <Center style={{ flex: 1, height: '100%' }}>
        <Stack align="center" gap="xs" maw={360} ta="center">
          <ThemeIcon variant="light" size={56} radius="xl">
            <IconBrandWhatsapp size={28} />
          </ThemeIcon>
          <Title order={4}>{t('Chat_PickConversation')}</Title>
          <Text c="dimmed" size="sm">
            {t('Chat_PickConversationDescription')}
          </Text>
          <Group gap={6} mt="sm" c="dimmed">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            <Text size="xs">to navigate the list</Text>
          </Group>
        </Stack>
      </Center>
    </Card>
  );
}

/* ───────────────────────── Conversation ───────────────────────── */

function Conversation({
  contactId,
  channel,
  onOpenSidebar,
}: {
  contactId: number;
  channel: ChannelKind;
  onOpenSidebar: () => void;
}) {
  const utils = trpc.useUtils();
  // Contact / CRM detail only — the message timeline is its own keyset infinite
  // query below (lazy-loads older messages on scroll-up).
  const bundle = trpc.chat.openingBundle.useQuery({ contactId });
  const thread = trpc.chat.messages.useInfiniteQuery(
    { contactId, limit: THREAD_PAGE_LIMIT },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const messages = useMemo(() => flattenThread(thread.data), [thread.data]);

  // Apply ONE message to the open thread — the fast path. Every send mutation
  // resolves with the full DTO, and most realtime broadcasts carry it too, so
  // the bubble appears with no extra request. Re-applying the same row is a
  // no-op, so a pushed message and a later reconciliation can't double-insert.
  const applyMessage = useCallback(
    (message: ThreadMessage) =>
      utils.chat.messages.setInfiniteData({ contactId, limit: THREAD_PAGE_LIMIT }, (old) =>
        upsertMessage(old, message),
      ),
    [utils, contactId],
  );
  // Reconciliation for events that carry no message payload (an echo of a send
  // made from another BSP client, a campaign batch). Merges the newest page
  // into the infinite cache — never invalidate, that refetches every loaded
  // page and drops a row at the shifted keyset boundary. TIP_FETCH_OPTS is
  // load-bearing: without it `fetch` resolves from the 30s-fresh cache entry
  // and the thread silently stops updating.
  const refreshThread = useCallback(
    () =>
      utils.chat.messages
        .fetch({ contactId, limit: THREAD_PAGE_LIMIT }, TIP_FETCH_OPTS)
        .then((tip) =>
          utils.chat.messages.setInfiniteData({ contactId, limit: THREAD_PAGE_LIMIT }, (old) =>
            mergeNewestPage(old, tip),
          ),
        ),
    [utils, contactId],
  );
  // Fire-and-forget wrapper for the mutation paths that have no DTO to apply.
  const reconcileThread = useCallback(() => {
    void refreshThread().catch(() => {});
  }, [refreshThread]);
  const dropFromThread = useCallback(
    (id: number) =>
      utils.chat.messages.setInfiniteData({ contactId, limit: THREAD_PAGE_LIMIT }, (old) =>
        removeMessageFromThread(old, id),
      ),
    [utils, contactId],
  );
  useChatThreadRealtime(contactId, {
    applyMessage,
    fetchTip: refreshThread,
    onDelete: dropFromThread,
  });

  const { actor: typingActor } = useTypingIndicator(contactId);
  // Look up the actor's display name from the contact's assignable-users list
  // (already fetched by the assignment popover). For bot-fired indicators we
  // skip the lookup and show "Bot".
  const assignableUsers = trpc.chat.assignableUsers.useQuery(undefined, {
    enabled: typingActor?.kind === 'user',
    staleTime: 5 * 60_000,
  });
  const typingLabel =
    typingActor?.kind === 'bot'
      ? 'Bot is typing…'
      : typingActor?.kind === 'user'
        ? `${
            (assignableUsers.data ?? []).find(
              (u: { id: string; name: string }) => u.id === typingActor.id,
            )?.name ?? 'A teammate'
          } is typing…`
        : null;
  // Every send resolves with the complete message DTO, so the bubble is
  // applied straight to the thread cache — one round-trip, no follow-up
  // fetch. (The realtime broadcast for our own send then arrives and
  // upserts the same row, which is a no-op.)
  const sendText = trpc.chat.sendText.useMutation({
    onSuccess: (dto) => {
      applyMessage(dto);
      void utils.chat.contacts.invalidate();
    },
    onError: (err) => {
      if (err.data?.sentinel === 'session_window_closed') {
        notifications.show({
          color: 'orange',
          message: 'Outside the 24h window — pick a template instead',
        });
      } else if (err.data?.sentinel === 'WHATSAPP_TOKEN_EXPIRED') {
        notifications.show({ color: 'red', message: err.message, autoClose: 10_000 });
      } else {
        notifications.show({ color: 'red', message: err.message });
      }
    },
  });
  // M2 — eager refresh on mark-as-read. The mutation itself is a single
  // SQL update; we trigger a contacts + totalUnread invalidation on
  // settled so the sidebar badge clears within one round-trip (without
  // waiting for a webhook-driven realtime broadcast). Coupled with the
  // 30s polling backstop this keeps the sidebar fresh even when the
  // websocket is down.
  const markAsRead = trpc.chat.markAsRead.useMutation({
    onSettled: () => {
      void utils.chat.contacts.invalidate();
      void utils.chat.totalUnread.invalidate();
    },
  });
  const deleteMsg = trpc.chat.deleteMessage.useMutation({
    // Returns `true`, not a row — reconcile. (The MessageDeleted broadcast
    // removes it from every other viewer's cache directly.)
    onSuccess: () => reconcileThread(),
  });
  const editMsg = trpc.chat.editMessage.useMutation({
    onSuccess: (dto) => applyMessage(dto),
    onError: (err) => {
      if (err.data?.sentinel === 'edit_window_expired') {
        notifications.show({ color: 'orange', message: 'Edit window has closed (15 min)' });
      } else {
        notifications.show({ color: 'red', message: err.message });
      }
    },
  });
  const togglePin = trpc.chat.togglePin.useMutation({
    onSuccess: () => {
      void utils.chat.openingBundle.invalidate({ contactId });
      void utils.chat.contacts.invalidate();
      void utils.contacts.contacts.list.invalidate();
    },
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });
  const sendReaction = trpc.chat.sendReaction.useMutation({
    onSuccess: (dto) => applyMessage(dto),
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });
  const sendMediaUpload = trpc.chat.sendMediaUpload.useMutation({
    onSuccess: (dto) => applyMessage(dto),
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });
  const sendMediaFromLibrary = trpc.chat.sendMediaFromLibrary.useMutation({
    onSuccess: (dto) => applyMessage(dto),
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });
  const [libraryOpen, libraryCtl] = useDisclosure(false);
  const [templateModalOpen, templateModalCtl] = useDisclosure(false);
  const sendForm = trpc.forms.send.useMutation({
    onSuccess: () => {
      reconcileThread();
      notifications.show({ color: 'green', message: 'Form sent' });
    },
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });

  // Per-contact draft persistence — switching conversation keeps text.
  const [drafts, setDrafts] = useLocalStorage<Record<string, string>>({
    key: 'leadsync.chat.drafts',
    defaultValue: {},
  });
  const draft = drafts[String(contactId)] ?? '';

  // Outbound typing indicator — fire `chat.sendTypingIndicator` at most once
  // per 10 seconds of active typing per contact. The server's own
  // implementation drops the call if there's no inbound wamid or the
  // 25-second Meta window has lapsed, so we don't need to be precise about
  // when it would actually land — just stay throttled enough to avoid
  // flooding the api on every keystroke.
  const sendTypingIndicator = trpc.chat.sendTypingIndicator.useMutation();
  const lastTypingFiredAtRef = useRef<{ contactId: number; at: number }>({ contactId: 0, at: 0 });
  const setDraft = (next: string) => {
    setDrafts((prev) => ({ ...prev, [contactId]: next }));
    // Only fire when the user has actually typed something (transitions from
    // empty → non-empty or extends the existing draft). Treat backspace /
    // paste-clear as not-typing so we don't ping when the user is deleting.
    if (next.length > draft.length) {
      const now = Date.now();
      const last = lastTypingFiredAtRef.current;
      if (last.contactId !== contactId || now - last.at > 10_000) {
        lastTypingFiredAtRef.current = { contactId, at: now };
        sendTypingIndicator.mutate({ contactId });
      }
    }
  };

  const [replyTo, setReplyTo] = useState<{
    id: number;
    text: string;
    isIncoming: boolean;
  } | null>(null);
  const [showSlash, setShowSlash] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState<
    { kind: 'composer' } | { kind: 'reaction'; messageWamid: string } | null
  >(null);
  // Reset reply target when switching contacts.
  useEffect(() => {
    setReplyTo(null);
    setShowSlash(false);
  }, [contactId]);

  useEffect(() => {
    if (bundle.data && bundle.data.contact.unreadCount > 0) {
      markAsRead.mutate({ contactId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, bundle.data?.contact.unreadCount]);

  // Infinite-thread scroll: auto-load older on scroll-up, keep the read
  // position on prepend, and follow the newest message when at the bottom.
  const { viewportRef, topSentinelRef, onScroll } = useThreadScroll({
    messageCount: messages.length,
    newestId: messages.length ? messages[messages.length - 1]!.id : null,
    oldestId: messages.length ? messages[0]!.id : null,
    hasMore: thread.hasNextPage,
    isFetchingOlder: thread.isFetchingNextPage,
    fetchOlder: thread.fetchNextPage,
    conversationKey: contactId,
  });

  if (bundle.isPending) {
    return (
      <Card style={{ flex: 1 }}>
        <Skeleton height="100%" />
      </Card>
    );
  }
  if (bundle.isError || !bundle.data) {
    return <Text c="red">{bundle.error?.message ?? 'Failed to load'}</Text>;
  }

  const { contact } = bundle.data;
  const sessionClosed = !contact.isWindowOpen;
  const isPinned = contact.pinnedAt != null;
  const { timeline, reactionsByWamid } = dockReactions(messages);
  const grouped = groupMessages(timeline);
  const minutesLeft = contact.windowClosesInMinutes;

  return (
    <Card p={0} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Group p="sm" justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {/* Mobile-only hamburger to re-open the inbox sidebar */}
          <ActionIcon
            variant="subtle"
            size="md"
            onClick={onOpenSidebar}
            aria-label="Open inbox sidebar"
            className="chat-mobile-hamburger"
          >
            <IconChevronRight size={18} style={{ transform: 'rotate(180deg)' }} />
          </ActionIcon>
          <Avatar size="md" radius="xl">
            {(contactDisplayName(contact) || '?')[0]?.toUpperCase()}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <ChannelChip channel={channel} size={14} />
              <Text fw={600} truncate>
                {contactDisplayName(contact) || '—'}
              </Text>
            </Group>
            <Text c="dimmed" size="xs" ff="monospace" truncate>
              {contactIdentifier(contact) ?? '—'}
              {contact.assignedAgentName && ` · assigned to ${contact.assignedAgentName}`}
            </Text>
            {typingLabel && (
              <Text c="blue.6" size="xs" fs="italic" truncate>
                {typingLabel}
              </Text>
            )}
          </Box>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {channel === 'whatsapp' && (
            <WindowIndicator closed={sessionClosed} minutesLeft={minutesLeft} />
          )}
          <Tooltip label={isPinned ? 'Unpin conversation' : 'Pin conversation'}>
            <ActionIcon
              variant="subtle"
              color={isPinned ? 'blue' : undefined}
              loading={togglePin.isPending}
              onClick={() => togglePin.mutate({ contactId })}
              aria-label={isPinned ? 'Unpin conversation' : 'Pin conversation'}
            >
              {isPinned ? <IconPinFilled size={16} /> : <IconPin size={16} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Divider />

      {/* Messages */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          background: `
            radial-gradient(circle at 20% 10%, rgba(34, 139, 230, 0.04), transparent 40%),
            radial-gradient(circle at 80% 80%, rgba(34, 139, 230, 0.04), transparent 40%),
            light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-8))`,
        }}
      >
        <ScrollArea
          viewportRef={viewportRef}
          onScrollPositionChange={onScroll}
          h="100%"
          scrollbarSize={6}
        >
          <Stack gap={2} p="md">
            {/* Top sentinel — scrolling it into view lazy-loads the next older
                page (keyset). Sits above the oldest bubble. */}
            <div ref={topSentinelRef} />
            {thread.isFetchingNextPage && (
              <Center py="xs">
                <Loader size="xs" />
              </Center>
            )}
            <AnimatePresence initial={false}>
              {grouped.map((group, gi) => (
                <Box key={`g-${gi}`}>
                  <DateDivider label={group.label} />
                  {group.bursts.map((burst, bi) => (
                    <Stack key={`b-${gi}-${bi}`} gap={2} mt={bi === 0 ? 0 : 8}>
                      {burst.messages.map((m, mi) => {
                        const isLast = mi === burst.messages.length - 1;
                        return (
                          <MessageBubble
                            key={m.id}
                            message={m}
                            channel={channel}
                            reactions={m.wamid ? reactionsByWamid.get(m.wamid) : undefined}
                            isFirstInBurst={mi === 0}
                            isLastInBurst={isLast}
                            onReply={() =>
                              setReplyTo({
                                id: m.id,
                                text: waPreviewText(m),
                                isIncoming: m.isIncoming,
                              })
                            }
                            onEdit={(text) => editMsg.mutate({ messageLogId: m.id, newText: text })}
                            onDelete={() => deleteMsg.mutate({ messageLogId: m.id })}
                            onReact={(wamid) =>
                              setEmojiTarget({ kind: 'reaction', messageWamid: wamid })
                            }
                          />
                        );
                      })}
                    </Stack>
                  ))}
                </Box>
              ))}
            </AnimatePresence>
            {messages.length === 0 && thread.isPending && (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            )}
            {messages.length === 0 && !thread.isPending && (
              <Center py="xl">
                <Stack align="center" gap={4}>
                  <ThemeIcon variant="light" size={42} radius="xl">
                    <IconSparkles size={20} />
                  </ThemeIcon>
                  <Text c="dimmed" size="sm">
                    Send a message to start the conversation.
                  </Text>
                </Stack>
              </Center>
            )}
          </Stack>
        </ScrollArea>
      </Box>

      {/* Reply preview */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <Paper
              px="sm"
              py={6}
              radius={0}
              style={{
                borderTop:
                  '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
                borderLeft: '3px solid var(--mantine-color-blue-5)',
                background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ minWidth: 0 }}>
                  <Text size="xs" c="blue.6" fw={600}>
                    Replying to {replyTo.isIncoming ? contact.fullName || 'contact' : 'yourself'}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {replyTo.text}
                  </Text>
                </Box>
                <ActionIcon size="sm" variant="subtle" onClick={() => setReplyTo(null)}>
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Composer */}
      <Divider />
      <Composer
        channel={channel}
        sessionClosed={sessionClosed}
        draft={draft}
        setDraft={setDraft}
        sending={sendText.isPending}
        showSlash={showSlash}
        setShowSlash={setShowSlash}
        onOpenEmoji={() => setEmojiTarget({ kind: 'composer' })}
        onSendFile={(file) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            if (!base64) return;
            sendMediaUpload.mutate({
              contactId,
              bytesBase64: base64,
              mimeType: file.type || 'application/octet-stream',
              filename: file.name,
            });
          };
          reader.readAsDataURL(file);
        }}
        fileSending={sendMediaUpload.isPending}
        onPickFromLibrary={libraryCtl.open}
        onOpenTemplates={contact.waId ? templateModalCtl.open : undefined}
        onSendForm={
          contact.waId
            ? (flowUid) => sendForm.mutate({ uid: flowUid, to: contact.waId!, contactId })
            : undefined
        }
        onSubmit={() => {
          const text = draft.trim();
          if (!text) return;
          sendText.mutate(
            {
              contactId,
              text,
              replyToMessageLogId: replyTo?.id,
            },
            {
              onSuccess: () => {
                setDraft('');
                setReplyTo(null);
              },
            },
          );
        }}
      />

      <MediaLibraryPicker
        opened={libraryOpen}
        onClose={libraryCtl.close}
        busy={sendMediaFromLibrary.isPending}
        onSelect={(item) => {
          sendMediaFromLibrary.mutate({ contactId, mediaAssetId: item.id });
          libraryCtl.close();
        }}
      />

      <SendTemplateModal
        opened={templateModalOpen}
        onClose={templateModalCtl.close}
        contactId={contactId}
        onSent={reconcileThread}
      />

      {/* Lazy emoji picker — single modal services both the composer and the
          per-message reaction button. The current emojiTarget knows where to
          deliver the chosen emoji. */}
      <Modal
        opened={emojiTarget !== null}
        onClose={() => setEmojiTarget(null)}
        withCloseButton={false}
        padding={0}
        size="auto"
        centered
        zIndex={1000}
      >
        <Suspense fallback={<Loader m="xl" />}>
          <EmojiPicker
            onEmojiClick={(e) => {
              if (!emojiTarget) return;
              if (emojiTarget.kind === 'composer') {
                setDraft(draft + e.emoji);
              } else {
                sendReaction.mutate({
                  contactId,
                  targetWamid: emojiTarget.messageWamid,
                  emoji: e.emoji,
                });
              }
              setEmojiTarget(null);
            }}
          />
        </Suspense>
      </Modal>
    </Card>
  );
}

/* ───────────────────────── Window indicator ───────────────────────── */

function WindowIndicator({ closed, minutesLeft }: { closed: boolean; minutesLeft: number | null }) {
  const { t } = useTranslation();
  if (closed) {
    return (
      <Tooltip label={t('Chat_WindowClosedTooltip')}>
        <Badge color="orange" variant="light" leftSection={<IconClock size={12} />}>
          {t('Chat_WindowClosed')}
        </Badge>
      </Tooltip>
    );
  }
  if (minutesLeft == null) return null;
  const total = 24 * 60;
  const used = Math.max(0, total - minutesLeft);
  const pct = Math.min(100, (used / total) * 100);
  const urgent = minutesLeft < 60;
  const label =
    minutesLeft < 60
      ? `${minutesLeft}m left`
      : `${Math.floor(minutesLeft / 60)}h ${minutesLeft % 60}m left`;

  return (
    <Tooltip label={`24h window: ${label}`}>
      <Group gap={6} wrap="nowrap">
        <RingProgress
          size={28}
          thickness={3}
          sections={[{ value: pct, color: urgent ? 'orange' : 'green' }]}
        />
        <Text size="xs" c={urgent ? 'orange.7' : 'green.7'} fw={500}>
          {label}
        </Text>
      </Group>
    </Tooltip>
  );
}

/* ───────────────────────── Message bubble ───────────────────────── */

type MessageT = {
  id: number;
  isIncoming: boolean;
  message: string | null;
  messageType: string | null;
  status: string;
  messagedAt: Date | string | null;
  editCount: number;
  wamid: string | null;
  // Free-form metadata. For outbound `interactive` bubbles the cascade
  // engine writes `{ source: 'bot', interactiveType: 'button', buttons: [{id,title}] }`
  // here so the chat UI can render the button list alongside the prompt.
  // For media/template the engine includes media url / template name.
  dataJson?: string | null;
  // Optional — server doesn't surface a reply preview yet but the bubble
  // renders it the moment the DTO grows the field.
  replyTo?: { id: number; preview: string; isIncoming: boolean } | null;
  // v25-audit DTO fields surfaced by chat-service.toMessageDto.
  forwarded?: boolean | null;
  frequentlyForwarded?: boolean | null;
  referralSourceUrl?: string | null;
  referralHeadline?: string | null;
  referralBody?: string | null;
  reactionEmoji?: string | null;
  // Meta's errors[] reason captured for `unsupported` inbound messages.
  errorMessage?: string | null;
  // Meta's errors[] code — 131051 ("Cloud API can't represent this type") vs
  // 131060 ("sent before this number was connected") read very differently to
  // an agent, so the soft note distinguishes them.
  errorCode?: number | null;
  // Target wamid for `reaction` rows — used to dock the emoji onto the bubble
  // it reacted to instead of rendering a standalone bubble.
  reactedToMessageId?: string | null;
  // Raw inbound Meta payload — carries nfm_reply for flow-submission cards.
  payloadJson?: string | null;
  // Durable URL for mirrored inbound media (image/video/audio/document/sticker).
  mediaUrl?: string | null;
};

function MessageBubble({
  message: m,
  channel,
  reactions,
  isFirstInBurst,
  isLastInBurst,
  onReply,
  onEdit,
  onDelete,
  onReact,
}: {
  message: MessageT;
  channel: ChannelKind;
  /** Emoji docked onto this bubble by `dockReactions`. */
  reactions?: string[];
  isFirstInBurst: boolean;
  isLastInBurst: boolean;
  onReply: () => void;
  onEdit: (text: string) => void;
  onDelete: () => void;
  onReact: (wamid: string) => void;
}) {
  const incoming = m.isIncoming;
  const radius = 14;
  const tail = 4;
  const borderRadius = incoming
    ? `${isFirstInBurst ? radius : tail}px ${radius}px ${radius}px ${
        isLastInBurst ? radius : tail
      }px`
    : `${radius}px ${isFirstInBurst ? radius : tail}px ${
        isLastInBurst ? radius : tail
      }px ${radius}px`;

  // One parse of this message's Meta payload, shared by the body renderer,
  // the card renderer and every capability check below. Replaces the old
  // ternary ladder + eight independently-parsing card blocks.
  const view = useMemo(() => normalizeWaMessage(m), [m]);
  const canReact = channel === 'whatsapp' && view.capabilities.reactable;
  const canEdit = view.capabilities.editable;

  // System/call events render as a centred pill rather than a chat bubble.
  // `capabilities.pill` is derived once in the normalizer, so the thread never
  // has to enumerate message types to decide.
  if (view.capabilities.pill) {
    return (
      <motion.div
        layout
        id={`activity-msg-${m.id}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <WaMessageBody view={view} incoming={incoming} />
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      id={`activity-msg-${m.id}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.15 }}
      style={{ display: 'flex', justifyContent: incoming ? 'flex-start' : 'flex-end' }}
    >
      <Box
        className="chat-bubble-row"
        style={{
          maxWidth: '72%',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 4,
          flexDirection: incoming ? 'row' : 'row-reverse',
        }}
      >
        <Stack gap={4} style={{ minWidth: 0, flex: '0 1 auto' }}>
          <Paper
            shadow="none"
            py={6}
            px={10}
            style={{
              borderRadius,
              // `light-dark()` resolves against the document's `color-scheme`
              // declaration that Mantine sets via <ColorSchemeScript />. This
              // is more reliable than `var(--mantine-color-default)` which
              // sometimes lags the scheme flip in portal contexts and leaves
              // a light bubble paired with light text → invisible content.
              background: incoming
                ? 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))'
                : 'var(--mantine-color-blue-6)',
              color: incoming
                ? 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0))'
                : 'white',
              border: incoming
                ? '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))'
                : 'none',
              position: 'relative',
            }}
          >
            {m.replyTo && (
              <Box
                style={{
                  borderLeft: `3px solid ${
                    incoming ? 'var(--mantine-color-blue-5)' : 'rgba(255,255,255,0.6)'
                  }`,
                  paddingLeft: 6,
                  marginBottom: 4,
                  opacity: 0.8,
                }}
              >
                <Text size="xs" fw={600}>
                  {m.replyTo.isIncoming ? 'Contact' : 'You'}
                </Text>
                <Text size="xs" lineClamp={2}>
                  {m.replyTo.preview}
                </Text>
              </Box>
            )}
            {/* S2 — forwarded / frequently-forwarded chip */}
            {incoming && (m.forwarded || m.frequentlyForwarded) && (
              <Group gap={4} mb={4}>
                <Text size="xs" c={incoming ? 'dimmed' : 'rgba(255,255,255,0.85)'} fs="italic">
                  ↪ {m.frequentlyForwarded ? 'Frequently forwarded' : 'Forwarded'}
                </Text>
              </Group>
            )}
            {/* S3 — click-to-WhatsApp ad attribution chip */}
            {incoming && m.referralSourceUrl && (
              <Box
                mb={4}
                style={{
                  padding: '4px 6px',
                  borderRadius: 6,
                  background: incoming
                    ? 'var(--mantine-color-blue-light)'
                    : 'rgba(255,255,255,0.1)',
                  fontSize: 11,
                }}
              >
                <Text size="xs" fw={600} c={incoming ? 'blue.7' : 'white'}>
                  Click-to-WhatsApp ad
                </Text>
                {m.referralHeadline && (
                  <Text size="xs" lineClamp={2}>
                    {m.referralHeadline}
                  </Text>
                )}
                {m.referralBody && (
                  <Text size="xs" c={incoming ? 'dimmed' : 'rgba(255,255,255,0.85)'} lineClamp={2}>
                    {m.referralBody}
                  </Text>
                )}
              </Box>
            )}
            <WaMessageBody view={view} incoming={incoming} />
            {isLastInBurst && (
              <Group gap={4} justify="flex-end" mt={2} wrap="nowrap">
                {m.editCount > 0 && (
                  <Text size="xs" c={incoming ? 'dimmed' : 'rgba(255,255,255,0.7)'} fs="italic">
                    edited
                  </Text>
                )}
                <Text size="xs" c={incoming ? 'dimmed' : 'rgba(255,255,255,0.85)'}>
                  {m.messagedAt ? formatTime(new Date(m.messagedAt)) : ''}
                </Text>
                {!incoming && <ReadReceipt status={m.status} />}
              </Group>
            )}
          </Paper>
          {/* Reaction pill, docked to the bubble it belongs to and overlapping
              its bottom edge — the way WhatsApp renders it. Lifted out of the
              timeline by `dockReactions`. */}
          {reactions && reactions.length > 0 && (
            <Group
              gap={2}
              mt={-12}
              mb={2}
              style={{
                alignSelf: incoming ? 'flex-start' : 'flex-end',
                marginLeft: incoming ? 8 : undefined,
                marginRight: incoming ? undefined : 8,
                padding: '1px 6px',
                borderRadius: 999,
                fontSize: 13,
                lineHeight: 1.4,
                zIndex: 1,
                background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-5))',
                border:
                  '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
              }}
            >
              {reactions.map((emoji) => (
                <span key={emoji}>{emoji}</span>
              ))}
            </Group>
          )}
          <WaMessageCard view={view} incoming={incoming} />
        </Stack>
        <Group gap={2} className="chat-bubble-actions" style={{ opacity: 0 }}>
          <Tooltip label="Reply">
            <ActionIcon size="sm" variant="subtle" onClick={onReply} aria-label="Reply to message">
              <IconArrowBackUp size={14} />
            </ActionIcon>
          </Tooltip>
          {canReact && (
            <Tooltip label="React">
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => onReact(m.wamid!)}
                aria-label="React to message"
              >
                <IconMoodHappy size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          <Menu position={incoming ? 'right' : 'left'}>
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={onReply}>
                Reply
              </Menu.Item>
              {m.message && (
                <Menu.Item
                  leftSection={<IconCopy size={14} />}
                  onClick={() => {
                    void navigator.clipboard.writeText(m.message ?? '').then(
                      () =>
                        notifications.show({
                          color: 'green',
                          message: 'Message copied',
                          autoClose: 1500,
                        }),
                      () =>
                        notifications.show({
                          color: 'red',
                          message: 'Copy failed — clipboard not available',
                        }),
                    );
                  }}
                >
                  Copy
                </Menu.Item>
              )}
              {canEdit && (
                <Menu.Item
                  onClick={() => {
                    const next = window.prompt('Edit message', m.message ?? '');
                    if (next && next !== m.message) onEdit(next);
                  }}
                >
                  Edit
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
                Delete for me
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Box>
    </motion.div>
  );
}

function ReadReceipt({ status }: { status: string }) {
  if (status === 'failed') {
    return (
      <Tooltip label="Failed to send">
        <Text size="xs" c="red.3">
          ✕
        </Text>
      </Tooltip>
    );
  }
  if (status === 'read') {
    return (
      <Tooltip label="Read">
        <IconChecks size={14} style={{ color: '#8ed4ff' }} />
      </Tooltip>
    );
  }
  if (status === 'delivered') {
    return (
      <Tooltip label="Delivered">
        <IconChecks size={14} style={{ color: 'rgba(255,255,255,0.7)' }} />
      </Tooltip>
    );
  }
  if (status === 'sent') {
    return (
      <Tooltip label="Sent">
        <IconCheck size={14} style={{ color: 'rgba(255,255,255,0.7)' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip label="Sending">
      <IconClock size={12} style={{ color: 'rgba(255,255,255,0.6)' }} />
    </Tooltip>
  );
}

function DateDivider({ label }: { label: string }) {
  return (
    <Center my="sm">
      <Box
        style={{
          padding: '4px 12px',
          borderRadius: 999,
          background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
          border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--mantine-color-dimmed)',
          backdropFilter: 'blur(4px)',
        }}
      >
        {label}
      </Box>
    </Center>
  );
}

/* ───────────────────────── Composer ───────────────────────── */

function Composer({
  channel,
  sessionClosed,
  draft,
  setDraft,
  sending,
  showSlash,
  setShowSlash,
  onOpenEmoji,
  onSendFile,
  fileSending,
  onPickFromLibrary,
  onOpenTemplates,
  onSendForm,
  onSubmit,
}: {
  channel: ChannelKind;
  sessionClosed: boolean;
  draft: string;
  setDraft: (v: string) => void;
  sending: boolean;
  showSlash: boolean;
  setShowSlash: (v: boolean) => void;
  onOpenEmoji: () => void;
  onSendFile: (file: File) => void;
  fileSending: boolean;
  /** Open the media-library picker (WhatsApp only — re-send a stored asset). */
  onPickFromLibrary?: () => void;
  /** Open the "Send a template" modal (WhatsApp only — undefined when the
   *  contact has no waId). This is the only way to message a contact once
   *  the 24h session window has closed. */
  onOpenTemplates?: () => void;
  /** Send a published WhatsApp form (undefined when the contact has no waId). */
  onSendForm?: (flowUid: string) => void;
  onSubmit: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerDisabled = channel === 'whatsapp' && sessionClosed;
  const isWa = channel === 'whatsapp';
  const placeholder =
    channel === 'whatsapp'
      ? sessionClosed
        ? 'Outside 24h window — open templates to re-engage'
        : 'Type a message…  (press / for templates)'
      : `Type a ${CHANNEL_META[channel].label} message…`;

  // Slash-menu state lives here so the textarea keydown handler can drive
  // selection (↑ / ↓ to navigate, Tab / Enter to pick).
  const slashList = trpc.chat.quickReplies.list.useQuery(undefined, { enabled: showSlash });
  const slashQuery = draft.startsWith('/') ? draft.slice(1).toLowerCase() : '';
  const slashItems = useMemo(() => {
    const items = slashList.data ?? [];
    if (!slashQuery) return items;
    return items.filter(
      (q) =>
        q.title.toLowerCase().includes(slashQuery) ||
        (q.body ?? '').toLowerCase().includes(slashQuery),
    );
  }, [slashList.data, slashQuery]);
  const [highlight, setHighlight] = useState(0);
  // Reset / clamp the highlight whenever the filtered list shrinks or the
  // menu re-opens — avoids stale indices pointing past the end.
  useEffect(() => {
    setHighlight((h) => {
      if (slashItems.length === 0) return 0;
      return Math.min(h, slashItems.length - 1);
    });
  }, [slashItems.length, showSlash]);

  const pickSlash = (q: { body: string | null }) => {
    setDraft(q.body ?? '');
    setShowSlash(false);
    setHighlight(0);
  };

  return (
    <Box style={{ position: 'relative' }}>
      <SlashMenu
        opened={showSlash}
        items={slashItems}
        highlight={highlight}
        isLoading={slashList.isPending}
        query={slashQuery}
        onHighlight={setHighlight}
        onPick={pickSlash}
        onClose={() => setShowSlash(false)}
      />

      <Group p="sm" wrap="nowrap" gap={6} align="flex-end">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onSendFile(file);
            e.target.value = '';
          }}
        />
        <Menu position="top-start" withinPortal shadow="md">
          <Menu.Target>
            <Tooltip label={isWa ? 'Attach' : 'Attachments via dedicated inbox'}>
              <ActionIcon
                size="lg"
                variant="subtle"
                disabled={!isWa}
                radius="xl"
                loading={fileSending}
              >
                <IconPaperclip size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconUpload size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload from device
            </Menu.Item>
            {onPickFromLibrary && (
              <Menu.Item leftSection={<IconPhoto size={16} />} onClick={onPickFromLibrary}>
                Choose from library
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
        <QuickRepliesPopover
          channel="whatsapp"
          onPick={(body) => setDraft(draft ? `${draft} ${body}` : body)}
        />
        {isWa && onSendForm && <FormPickerPopover onPick={onSendForm} />}
        {isWa && onOpenTemplates && (
          <Tooltip label="Send a template">
            <ActionIcon size="lg" variant="subtle" radius="xl" onClick={onOpenTemplates}>
              <IconTemplate size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Emoji">
          <ActionIcon size="lg" variant="subtle" radius="xl" onClick={onOpenEmoji}>
            <IconMoodSmile size={18} />
          </ActionIcon>
        </Tooltip>
        <Textarea
          flex={1}
          autosize
          minRows={1}
          maxRows={6}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setDraft(v);
            if (v.startsWith('/')) setShowSlash(true);
            else if (showSlash) setShowSlash(false);
          }}
          onKeyDown={(e) => {
            // While the slash menu is open with results, the textarea
            // delegates ↑/↓/Tab/Enter to selection instead of cursor /
            // newline / submit.
            if (showSlash && slashItems.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => (h + 1) % slashItems.length);
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => (h - 1 + slashItems.length) % slashItems.length);
                return;
              }
              if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
                e.preventDefault();
                const target = slashItems[highlight] ?? slashItems[0];
                if (target) pickSlash(target);
                return;
              }
            }
            if (e.key === 'Escape') setShowSlash(false);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          disabled={composerDisabled}
          styles={{ input: { borderRadius: 18, paddingTop: 8, paddingBottom: 8 } }}
        />
        <Tooltip label="Send (Enter)">
          <ActionIcon
            size={42}
            radius="xl"
            variant="filled"
            loading={sending}
            disabled={!draft.trim() || composerDisabled}
            onClick={onSubmit}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}

type SlashItem = { id: number; title: string; body: string | null };

/** Floating slash panel above the composer. Presentational — selection state
 *  lives in the parent so the textarea can drive it via ↑/↓/Tab/Enter.
 *  When the query yields no matches we surface an inline create affordance
 *  so the agent can save the typed text as a new snippet without opening
 *  the templates popover. */
function SlashMenu({
  opened,
  items,
  highlight,
  isLoading,
  query,
  onHighlight,
  onPick,
  onClose,
}: {
  opened: boolean;
  items: SlashItem[];
  highlight: number;
  isLoading: boolean;
  query: string;
  onHighlight: (i: number) => void;
  onPick: (q: SlashItem) => void;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const create = trpc.chat.quickReplies.create.useMutation({
    onSuccess: () => void utils.chat.quickReplies.list.invalidate(),
    onError: (err) => notifications.show({ color: 'red', message: err.message }),
  });
  const showCreate = opened && !isLoading && items.length === 0 && query.trim().length > 0;
  // Keep the highlighted row visible when the user arrows past the fold.
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  useEffect(() => {
    const el = itemRefs.current[highlight];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, opened]);

  return (
    <AnimatePresence>
      {opened && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            left: 12,
            right: 12,
            zIndex: 10,
          }}
        >
          <Paper withBorder shadow="md" p="xs" radius="md">
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Quick replies
              </Text>
              <Group gap={6}>
                <Text size="xs" c="dimmed">
                  <Kbd>↑</Kbd> <Kbd>↓</Kbd> nav · <Kbd>Tab</Kbd> insert
                </Text>
                <ActionIcon size="xs" variant="subtle" onClick={onClose}>
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            </Group>
            {isLoading ? (
              <Skeleton height={48} />
            ) : showCreate ? (
              <Stack gap={6} py={4}>
                <Text size="xs" c="dimmed">
                  No snippet matches{' '}
                  <Text component="span" fw={600}>
                    /{query}
                  </Text>{' '}
                  yet.
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconCirclePlus size={12} />}
                  loading={create.isPending}
                  onClick={() =>
                    create.mutate(
                      {
                        scope: 'vendor',
                        title: query.trim(),
                        body: `/${query.trim()}`,
                        contentType: 'text',
                        isPinned: false,
                        sortOrder: 0,
                      },
                      {
                        onSuccess: () =>
                          notifications.show({
                            color: 'green',
                            message: `Snippet "/${query.trim()}" saved — open the templates icon to edit the body.`,
                          }),
                      },
                    )
                  }
                >
                  Save "/{query.trim()}" as a new snippet
                </Button>
              </Stack>
            ) : items.length === 0 ? (
              <Text size="xs" c="dimmed" py={6}>
                No saved snippets — type a name after `/` to create one.
              </Text>
            ) : (
              <ScrollArea.Autosize mah={220} type="auto">
                <Stack gap={2}>
                  {items.map((q, i) => {
                    const active = i === highlight;
                    return (
                      <UnstyledButton
                        key={q.id}
                        ref={(el) => {
                          itemRefs.current[i] = el;
                        }}
                        onClick={() => onPick(q)}
                        onMouseEnter={() => onHighlight(i)}
                        style={{
                          padding: '6px 8px',
                          borderRadius: 6,
                          background: active ? 'var(--mantine-color-blue-light)' : 'transparent',
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap" gap={6}>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate c={active ? 'blue.7' : undefined}>
                              /{q.title}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {q.body ?? '—'}
                            </Text>
                          </Box>
                          {active && <Kbd>↵</Kbd>}
                        </Group>
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Templates icon → opens a popover with the existing list + create / delete
 *  affordances. Mirrors the previous QuickRepliesButton behaviour so power
 *  users keep the inline create form. */
/** Forms icon → popover listing PUBLISHED WhatsApp forms; picking one sends
 *  the interactive form to the open contact. Only published forms are
 *  sendable to any contact (drafts are tester-only and live on the Forms page). */
function FormPickerPopover({ onPick }: { onPick: (flowUid: string) => void }) {
  const [opened, setOpened] = useState(false);
  const list = trpc.forms.list.useQuery(undefined, { enabled: opened });
  const published = (list.data ?? []).filter((f) => f.status === 'PUBLISHED');

  return (
    <Tooltip label="Send a form">
      <Box style={{ display: 'inline-flex' }}>
        <Popover
          opened={opened}
          onChange={setOpened}
          position="top-start"
          withArrow
          shadow="md"
          width={300}
        >
          <Popover.Target>
            <ActionIcon size="lg" variant="subtle" radius="xl" onClick={() => setOpened((o) => !o)}>
              <IconClipboardText size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Send a form
              </Text>
              {list.isPending ? (
                <Loader size="sm" />
              ) : published.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No published forms. Publish one under Outreach → Forms first.
                </Text>
              ) : (
                <Stack gap={4}>
                  {published.map((f) => (
                    <UnstyledButton
                      key={f.uid}
                      onClick={() => {
                        onPick(f.uid);
                        setOpened(false);
                      }}
                      style={{
                        padding: '6px 8px',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                      className="hoverable-row"
                    >
                      📋 {f.name}
                    </UnstyledButton>
                  ))}
                </Stack>
              )}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Box>
    </Tooltip>
  );
}

/* ───────────────────────── Helpers ───────────────────────── */

/**
 * Lift `reaction` rows out of the timeline and index them by the wamid they
 * point at. WhatsApp shows a reaction as a small pill docked to the message it
 * reacted to — never as a bubble of its own, which is what made a thread of
 * thumbs-ups unreadable here.
 *
 * A reaction whose target isn't on this page (older, not yet paged in) has
 * nowhere to dock, so it stays in the timeline rather than vanishing.
 */
function dockReactions(messages: MessageT[]): {
  timeline: MessageT[];
  reactionsByWamid: Map<string, string[]>;
} {
  const targets = new Set(
    messages.filter((m) => m.messageType !== 'reaction' && m.wamid).map((m) => m.wamid as string),
  );
  const reactionsByWamid = new Map<string, string[]>();
  const timeline: MessageT[] = [];

  for (const m of messages) {
    const target = m.messageType === 'reaction' ? (m.reactedToMessageId ?? null) : null;
    if (!target || !targets.has(target)) {
      timeline.push(m);
      continue;
    }
    const emoji = (m.reactionEmoji || m.message || '').trim();
    // Meta signals "reaction removed" by sending an empty emoji — clear the
    // pill rather than leaving a stale one docked.
    if (!emoji) {
      reactionsByWamid.set(target, []);
      continue;
    }
    const list = reactionsByWamid.get(target) ?? [];
    if (!list.includes(emoji)) list.push(emoji);
    reactionsByWamid.set(target, list);
  }
  return { timeline, reactionsByWamid };
}

function groupMessages(messages: MessageT[]) {
  // Group by date (label like "Today", "Yesterday", or "May 3"), then by
  // bursts of consecutive same-sender messages within ~3 minutes.
  const groups: { label: string; bursts: { isIncoming: boolean; messages: MessageT[] }[] }[] = [];
  let lastLabel = '';
  let lastBurst: { isIncoming: boolean; messages: MessageT[] } | null = null;
  let lastTime = 0;
  let currentGroup: (typeof groups)[number] | null = null;

  for (const m of messages) {
    const t = m.messagedAt ? new Date(m.messagedAt).getTime() : 0;
    const label = dayLabel(m.messagedAt ? new Date(m.messagedAt) : new Date());
    if (label !== lastLabel) {
      currentGroup = { label, bursts: [] };
      groups.push(currentGroup);
      lastLabel = label;
      lastBurst = null;
    }
    const sameBurst =
      lastBurst && lastBurst.isIncoming === m.isIncoming && t - lastTime < 3 * 60 * 1000;
    if (!sameBurst || !lastBurst) {
      lastBurst = { isIncoming: m.isIncoming, messages: [m] };
      currentGroup!.bursts.push(lastBurst);
    } else {
      lastBurst.messages.push(m);
    }
    lastTime = t;
  }
  return groups;
}

function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

// Tiny global rule so bubble actions reveal on hover. The previous chat used
// framer-motion's whileHover; a scoped CSS rule is lighter and doesn't fight
// Mantine's reset.
const styleId = '__chat_bubble_actions_css';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const el = document.createElement('style');
  el.id = styleId;
  el.textContent = `
    .chat-bubble-row:hover .chat-bubble-actions {
      opacity: 1 !important;
      transition: opacity 120ms ease;
    }
  `;
  document.head.appendChild(el);
}

function ChatRoute() {
  return (
    <PermissionGate permission="messaging">
      <ChatPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/chat')({
  component: ChatRoute,
  validateSearch: (s: Record<string, unknown>): { contactId?: number } => {
    const n = typeof s.contactId === 'number' ? s.contactId : Number(s.contactId);
    return Number.isFinite(n) && n > 0 ? { contactId: n } : {};
  },
});
